import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Alert,
} from '@mui/material';
import {
  Add, Edit as EditIcon, Delete as DeleteIcon, Close,
  GridView as GridViewIcon, TableChart as TableChartIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';

interface Category {
  _id?: string;
  id?: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  order: number;
  boxCount?: number;
}

const defaultColors = ['#FF6B35', '#0B6E4F', '#B1D65D', '#FF1654', '#FFA500', '#4CAF50', '#2196F3'];

export default function Categories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ total: number; boxCount: number } | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    color: defaultColors[0],
    order: 1,
  });

  const emojis = ['🥖', '☕', '🍽️', '🍰', '🍳', '🍱', '🛒'];

  useEffect(() => {
    fetchCategories();
  }, [page, rowsPerPage]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories', { params: { page, limit: rowsPerPage } });
      setCategories(response.data.categories || response.data || []);
      setTotal(response.data.total ?? response.data.categories?.length ?? 0);
      setServerStats(response.data.stats || null);
      setError(false);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        slug: category.slug,
        description: category.description,
        icon: category.icon,
        color: category.color,
        order: category.order,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        icon: '',
        color: defaultColors[0],
        order: categories.length + 1,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.slug || !formData.icon) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(`/api/categories/${editingCategory._id || editingCategory.id}`, formData);
        setSnackbar({ open: true, message: 'Category updated successfully', severity: 'success' });
      } else {
        await axios.post('/api/categories', formData);
        setSnackbar({ open: true, message: 'Category created successfully', severity: 'success' });
      }
      handleCloseDialog();
      fetchCategories();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save category', severity: 'error' });
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      await axios.delete(`/api/categories/${categoryId}`);
      setSnackbar({ open: true, message: 'Category deleted successfully', severity: 'success' });
      fetchCategories();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete category', severity: 'error' });
    }
  };

  // Prefer the backend's platform-wide counts so these cards don't
  // undercount once there's more than one page of categories.
  const stats = serverStats || {
    total: categories.length,
    boxCount: categories.reduce((sum, cat) => sum + (cat.boxCount || 0), 0),
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Categories</Typography>
          <Typography color="text.secondary">Manage food categories and organize your offerings.</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ display: 'flex', border: '1px solid #ddd', borderRadius: 1 }}>
            <IconButton
              size="small"
              onClick={() => setViewMode('grid')}
              sx={{
                bgcolor: viewMode === 'grid' ? 'primary.main' : 'transparent',
                color: viewMode === 'grid' ? 'white' : 'primary.main',
              }}
            >
              <GridViewIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewMode('table')}
              sx={{
                bgcolor: viewMode === 'table' ? 'primary.main' : 'transparent',
                color: viewMode === 'table' ? 'white' : 'primary.main',
              }}
            >
              <TableChartIcon />
            </IconButton>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
            Add Category
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Categories</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Boxes</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.boxCount}</Typography>
        </Paper>
      </Box>

      {/* View Mode: Grid */}
      {viewMode === 'grid' && (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        {categories.map((category) => {
          const catId = category._id || category.id;
          return (
            <Box key={catId}>
              <Card sx={{ textAlign: 'center', p: 2, position: 'relative' }}>
                <Box
                  sx={{
                    fontSize: 48,
                    mb: 1,
                    p: 1,
                    bgcolor: `${category.color}20`,
                    borderRadius: 2,
                    display: 'inline-block',
                  }}
                >
                  {category.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {category.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {category.description}
                </Typography>
                <Chip label={`${category.boxCount || 0} boxes`} size="small" sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <IconButton size="small" color="primary" onClick={() => handleOpenDialog(category)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(catId || '')}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Box>
          );
        })}
      </Box>
      )}

      {/* View Mode: Table */}
      {viewMode === 'table' && (
      <Card>
        <Typography variant="h6" sx={{ p: 2, fontWeight: 700 }}>All Categories</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell><strong>Icon</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Slug</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Boxes</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography>Loading categories...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No categories found.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && categories.map((category) => {
                const catId = category._id || category.id;
                return (
                  <TableRow key={catId} hover>
                    <TableCell>
                      <Box sx={{ fontSize: 32 }}>{category.icon}</Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{category.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={category.slug} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{category.description?.substring(0, 40)}...</TableCell>
                    <TableCell>{category.boxCount || 0}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" onClick={() => handleOpenDialog(category)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(catId || '')}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      )}

      <PaginationControls page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }} />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Add New Category'}
          <IconButton onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Category Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />

            <TextField
              fullWidth
              label="Slug (URL-friendly) *"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Emoji Icon *</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {emojis.map((emoji) => (
                  <Button
                    key={emoji}
                    variant={formData.icon === emoji ? 'contained' : 'outlined'}
                    sx={{ fontSize: 32, p: 1, minWidth: 'auto' }}
                    onClick={() => setFormData({ ...formData, icon: emoji })}
                  >
                    {emoji}
                  </Button>
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {defaultColors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: color,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: formData.color === color ? '3px solid #000' : '1px solid #ccc',
                    }}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              type="number"
              label="Display Order"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
